import React from 'react';

const gradeColor = (grade) => {
  if (!grade) return '#2e7d32';
  const g = grade.toUpperCase();
  if (!/^[ABCD]/.test(g)) return '#1565c0';
  if (g.startsWith('A')) return '#1b5e20';
  if (g.startsWith('B')) return '#2e7d32';
  if (g.startsWith('C')) return '#e65100';
  return '#b71c1c';
};

export default function ProjectsTable({ report, activities = [], showComments = true }) {
  // Extract project assessments from subjects_data
  const projectAssessments = React.useMemo(() => {
    if (!report?.subjects_data) return [];
    const projects = [];
    const projectKeywords = /^(proj|project|assign|assignment|prac|practical|coursework|cw)/i;
    
    report.subjects_data.forEach((sub) => {
      const subjectName = sub.subject?.subject_name || sub.subject?.name || 'Unknown Subject';
      (sub.papers || []).forEach((paper) => {
        (paper.assessments || []).forEach((a) => {
          const code = a.assessment_type?.assessment_code || a.assessment_code;
          if (code && projectKeywords.test(code)) {
            const raw = a.score ?? a.marks_obtained ?? a.raw_score;
            const maxMarks = a.max_marks ?? 100;
            const grade = a.assessment_grade || a.grade || '-';
            const remarks = a.remarks || a.comment || sub.subject_comment || '-';
            const teacher = paper.teacher_name || paper.teacher_initials || '-';
            
            projects.push({
              id: a.id,
              project_name: `${subjectName} - ${code}`,
              score: raw,
              max_score: maxMarks,
              grade: grade,
              remarks: remarks,
              supervisor: teacher
            });
          }
        });
      });
    });
    return projects;
  }, [report]);

  const allProjects = [...projectAssessments, ...(activities || [])];
  
  if (allProjects.length === 0) return null;

  return (
    <>
      <p className="rc-section-title">Projects & Co-Curricular Activities</p>

      <table className="rc-table">
        <thead>
          <tr>
            <th className="left" style={{ width: '30%' }}>Project/Activity</th>
            <th style={{ width: '12%', textAlign: 'center' }}>Score</th>
            <th style={{ width: '10%', textAlign: 'center' }}>Grade</th>
            {showComments && <th className="left" style={{ width: '38%' }}>Remarks</th>}
            <th style={{ width: '10%', textAlign: 'center' }}>Supervisor</th>
          </tr>
        </thead>
        <tbody>
          {allProjects.map((project, idx) => {
            const projectName = project.project_name || project.activity_name || project.name || `Project ${idx + 1}`;
            const score = project.score ?? project.marks ?? project.points;
            const maxScore = project.max_score ?? project.max_marks ?? 100;
            const grade = project.grade || project.rating || project.performance || '-';
            const remarks = project.remarks || project.comment || project.feedback || '-';
            const supervisor = project.supervisor || project.patron || project.teacher || project.instructor || '-';
            const isActivity = project.activity_name || project.participation_level;

            return (
              <tr key={project.id || idx}>
                <td className="subject-name">{projectName}</td>
                <td style={{ textAlign: 'center', fontSize: '8.5pt' }}>
                  {isActivity 
                    ? (project.participation_level || project.attendance || '-')
                    : (score != null ? `${Number(score).toFixed(0)}/${maxScore}` : '-')
                  }
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span className="rc-grade-badge" style={{ color: gradeColor(grade) }}>
                    {grade}
                  </span>
                </td>
                {showComments && (
                  <td style={{ fontSize: '8.5pt', fontStyle: 'italic', color: '#444' }}>{remarks}</td>
                )}
                <td style={{ textAlign: 'center', fontSize: '8.5pt' }}>{supervisor}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
